import { oc } from "@orpc/contract";
import { z } from "zod";
import { UserGroupStaticKind } from "@erp/authz";

// ============================================
// Shared group schemas
// ============================================

const OrganizationGroupIdInputSchema = z.object({
  groupId: z.string().uuid(),
});

const GroupStaticKindSchema = z.enum(UserGroupStaticKind).nullable();

// ============================================
// Get User Permissions Contract
// ============================================

export const GetUserPermissionsInputSchema = z.object({
  userId: z.string().uuid(),
});

const PermissionSchema = z.object({
  action: z.string(),
  subject: z.string(),
});

const GroupWithPermissionsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(PermissionSchema),
});

export const GetUserPermissionsOutputSchema = z.object({
  userId: z.string().uuid(),
  groups: z.array(GroupWithPermissionsSchema),
});

export const getUserPermissionsContract = oc
  .input(GetUserPermissionsInputSchema)
  .output(GetUserPermissionsOutputSchema)
  .route({
    method: "GET",
    summary: "Get a user's permissions by team",
    description:
      "Returns the teams (user groups) a user belongs to, each with its action and subject permissions. Users can view their own; admins can view anyone's.",
  });

// ============================================
// Delete Group Contract
// ============================================

export const DeleteGroupInputSchema = OrganizationGroupIdInputSchema;

export const DeleteGroupOutputSchema = z.object({
  success: z.literal(true),
});

export const deleteGroupContract = oc
  .input(DeleteGroupInputSchema)
  .output(DeleteGroupOutputSchema)
  .route({
    method: "POST",
    summary: "Delete a team",
    description:
      "Permanently deletes a user group (team) in the organization. Built-in groups such as Staff Admin cannot be deleted.",
  });

// ============================================
// List Groups Contract
// ============================================

export const UserGroupBasicInfoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  // The model stores a non-null string (empty when unset).
  description: z.string(),
  staticKind: GroupStaticKindSchema,
  userCount: z.number().int().nonnegative(),
});

export const ListUserGroupsOutputSchema = z.array(UserGroupBasicInfoSchema);

export const listUserGroupsContract = oc
  .output(ListUserGroupsOutputSchema)
  .route({
    method: "GET",
    summary: "List teams",
    description:
      "Returns the organization's user groups (teams) with ID, name, description, member count and static kind, sorted by name.",
  });

// ============================================
// Create Groups Contract
// ============================================

export const CreateGroupsInputSchema = z.object({
  groups: z
    .array(
      z.object({
        name: z.string().min(1).max(255),
        description: z.string().min(10).max(255),
      }),
    )
    .min(1),
});

const CreatedGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
});

export const CreateGroupsOutputSchema = z.object({
  groups: z.array(CreatedGroupSchema),
});

export const createGroupsContract = oc
  .input(CreateGroupsInputSchema)
  .output(CreateGroupsOutputSchema)
  .route({
    method: "POST",
    summary: "Create teams",
    description:
      "Creates one or more user groups (teams) in the organization and returns their IDs, names and descriptions.",
  });

// ============================================
// Edit Group Contract
// ============================================

export const EditGroupInputSchema = OrganizationGroupIdInputSchema.extend({
  name: z.string().min(1).max(255),
  description: z.string().max(255),
});

export const EditGroupOutputSchema = z.object({
  success: z.literal(true),
});

export const editGroupContract = oc
  .input(EditGroupInputSchema)
  .output(EditGroupOutputSchema)
  .route({
    method: "POST",
    summary: "Rename or re-describe a team",
    description:
      "Updates a team's name and description. Built-in groups such as Staff Admin cannot be renamed.",
  });

// ============================================
// Get Group Contract
// ============================================

export const GetGroupInputSchema = OrganizationGroupIdInputSchema;

const GroupUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
});

export const GroupDetailsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  staticKind: GroupStaticKindSchema,
  userCount: z.number().int().nonnegative(),
  users: z.array(GroupUserSchema),
});

export const GetGroupOutputSchema = z.object({
  group: GroupDetailsSchema,
});

export const getGroupContract = oc
  .input(GetGroupInputSchema)
  .output(GetGroupOutputSchema)
  .route({
    method: "GET",
    summary: "Get a team",
    description:
      "Returns a team's name, description, static kind, member count and members. Readers scoped to Staff Admin only can read that group alone.",
  });

// ============================================
// Get Group Members Contract
// ============================================

export const GetGroupMembersInputSchema = OrganizationGroupIdInputSchema;

const GroupMemberSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
});

export const GetGroupMembersOutputSchema = z.object({
  groupMembers: z.array(GroupMemberSchema),
});

export const getGroupMembersContract = oc
  .input(GetGroupMembersInputSchema)
  .output(GetGroupMembersOutputSchema)
  .route({
    method: "GET",
    summary: "List a team's members",
    description:
      "Returns the users on a team, by id and name. Readers scoped to Staff Admin only can read that group alone.",
  });

// ============================================
// Add Group Member Contract
// ============================================

export const AddGroupMemberInputSchema = OrganizationGroupIdInputSchema.extend({
  userId: z.string().uuid(),
});

export const AddGroupMemberOutputSchema = z.object({
  success: z.literal(true),
});

export const addGroupMemberContract = oc
  .input(AddGroupMemberInputSchema)
  .output(AddGroupMemberOutputSchema)
  .route({
    method: "POST",
    summary: "Add a user to a team",
    description:
      "Adds an organization member to a team. Only Gently staff can change a Gently staff member's team memberships.",
  });

// ============================================
// Remove Group Member Contract
// ============================================

export const RemoveGroupMemberInputSchema =
  OrganizationGroupIdInputSchema.extend({
    userId: z.string().uuid(),
  });

export const RemoveGroupMemberOutputSchema = z.object({
  success: z.literal(true),
});

export const removeGroupMemberContract = oc
  .input(RemoveGroupMemberInputSchema)
  .output(RemoveGroupMemberOutputSchema)
  .route({
    method: "POST",
    summary: "Remove a user from a team",
    description:
      "Removes an organization member from a team. The Staff Admin team's last member cannot be removed.",
  });

// ============================================
// Organization Groups contracts namespace
// ============================================

export const organizationGroupsContracts = {
  getUserPermissions: getUserPermissionsContract,
  deleteGroup: deleteGroupContract,
  list: listUserGroupsContract,
  create: createGroupsContract,
  edit: editGroupContract,
  get: getGroupContract,
  getMembers: getGroupMembersContract,
  addMember: addGroupMemberContract,
  removeMember: removeGroupMemberContract,
} as const;

// Export types
export type GetUserPermissionsInput = z.infer<
  typeof GetUserPermissionsInputSchema
>;
export type GetUserPermissionsOutput = z.infer<
  typeof GetUserPermissionsOutputSchema
>;
export type DeleteGroupInput = z.infer<typeof DeleteGroupInputSchema>;
export type DeleteGroupOutput = z.infer<typeof DeleteGroupOutputSchema>;
export type UserGroupBasicInfo = z.infer<typeof UserGroupBasicInfoSchema>;
export type ListUserGroupsOutput = z.infer<typeof ListUserGroupsOutputSchema>;
export type CreateGroupsInput = z.infer<typeof CreateGroupsInputSchema>;
export type CreateGroupsOutput = z.infer<typeof CreateGroupsOutputSchema>;
export type EditGroupInput = z.infer<typeof EditGroupInputSchema>;
export type EditGroupOutput = z.infer<typeof EditGroupOutputSchema>;
export type GetGroupInput = z.infer<typeof GetGroupInputSchema>;
export type GetGroupOutput = z.infer<typeof GetGroupOutputSchema>;
export type GroupDetails = z.infer<typeof GroupDetailsSchema>;
export type GetGroupMembersInput = z.infer<typeof GetGroupMembersInputSchema>;
export type GetGroupMembersOutput = z.infer<typeof GetGroupMembersOutputSchema>;
export type AddGroupMemberInput = z.infer<typeof AddGroupMemberInputSchema>;
export type AddGroupMemberOutput = z.infer<typeof AddGroupMemberOutputSchema>;
export type RemoveGroupMemberInput = z.infer<
  typeof RemoveGroupMemberInputSchema
>;
export type RemoveGroupMemberOutput = z.infer<
  typeof RemoveGroupMemberOutputSchema
>;
